import api from './api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return ((data as { data?: T })?.data ?? data) as T;
}

export type SupplierAdvanceRefundStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Posted'
  | 'Cancelled';

export interface RefundChargeLineDTO {
  charge_type: string;
  account_id: string;
  amount: number;
  currency?: string;
  tax_code?: string;
  tax_amount?: number;
  remarks?: string;
}

export interface SupplierAdvanceRefundChargeLine extends RefundChargeLineDTO {
  id?: string;
  account?: { id: string; account_code: string; account_name: string };
}

export interface SupplierAdvanceRefundVoucher {
  id: string;
  company_id?: string;
  voucher_number: string;
  refund_date: string;
  supplier_id: string;
  purchase_order_id: string;
  supplier_advance_request_id: string;
  recovery_exposure_id?: string;
  original_advance_amount: number;
  already_refunded: number;
  already_adjusted: number;
  refund_available: number;
  refund_amount: number;
  bank_received_amount: number;
  remaining_balance: number;
  advance_currency: string;
  refund_currency: string;
  base_currency: string;
  advance_exchange_rate?: number;
  refund_exchange_rate?: number;
  base_equivalent: number;
  exchange_difference: number;
  exchange_gain_loss_account_id?: string;
  receiving_account_id: string;
  advance_gl_account_id: string;
  reference_number?: string;
  narration?: string;
  status: SupplierAdvanceRefundStatus;
  approval_status?: string;
  approval_instance_id?: string | null;
  journal_voucher_id?: string;
  posted_at?: string;
  posted_by?: string;
  supplier?: { id: string; supplier_name: string; supplier_code?: string };
  purchaseOrder?: { id: string; po_number: string };
  supplierAdvanceRequest?: { id: string; advance_request_number: string };
  receivingAccount?: { id: string; account_code: string; account_name: string };
  advanceGlAccount?: { id: string; account_code: string; account_name: string };
  chargeLines?: SupplierAdvanceRefundChargeLine[];
}

export interface CreateSupplierAdvanceRefundPayload {
  recovery_exposure_id?: string;
  supplier_advance_request_id?: string;
  refund_date: string;
  refund_amount: number;
  bank_received_amount: number;
  receiving_account_id: string;
  advance_gl_account_id: string;
  refund_currency?: string;
  advance_currency?: string;
  advance_exchange_rate?: number;
  refund_exchange_rate?: number;
  exchange_gain_loss_account_id?: string;
  reference_number?: string;
  narration?: string;
  charge_lines?: RefundChargeLineDTO[];
}

export type UpdateSupplierAdvanceRefundPayload = Partial<CreateSupplierAdvanceRefundPayload>;

export interface SupplierAdvanceRefundFilters {
  supplier_id?: string;
  purchase_order_id?: string;
  supplier_advance_request_id?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
  voucher_number?: string;
}

export interface SupplierAdvanceRecoveryExposure {
  id: string;
  purchase_order_id: string;
  supplier_id: string;
  supplier_advance_request_id: string;
  po_number: string;
  advance_request_number: string;
  supplier_name?: string;
  currency: string;
  advance_paid: number;
  advance_adjusted: number;
  advance_refunded: number;
  remaining_refundable_balance: number;
  base_currency: string;
  base_currency_equivalent: number;
  recovery_status: string;
  cancellation_date?: string;
  cancellation_reason?: string;
  exposure_age_days: number;
  supplier?: { id: string; supplier_name: string };
  purchaseOrder?: { id: string; po_number: string };
  supplierAdvanceRequest?: { id: string; advance_request_number: string };
}

export interface RecoveryExposureFilters {
  recovery_status?: string;
  supplier_id?: string;
  purchase_order_id?: string;
}

export interface SupplierAdvanceRecoveryQueueRow {
  exposure_id: string;
  purchase_order_id: string;
  po_number: string;
  supplier_id: string;
  supplier_name?: string;
  supplier_advance_request_id: string;
  advance_request_number: string;
  remaining_refundable_balance: number;
  recovery_status: string;
  exposure_age_days: number;
  cancellation_date?: string;
  link_path: string;
}

export interface SupplierAdvanceRecoveryDashboardSummary {
  pending_count: number;
  partial_count: number;
  overdue_count: number;
  exposure_total: number;
  queue_rows: SupplierAdvanceRecoveryQueueRow[];
}

export interface SupplierAdvanceRefundPrintData {
  voucher_number: string;
  refund_date: string | null;
  status: string;
  supplier_id: string;
  supplier_name?: string;
  purchase_order_id: string;
  po_number?: string;
  advance_request_number?: string;
  original_advance_amount: number;
  already_refunded: number;
  already_adjusted: number;
  refund_available: number;
  refund_amount: number;
  bank_received_amount: number;
  remaining_balance: number;
  advance_currency: string;
  refund_currency: string;
  base_currency: string;
  advance_exchange_rate?: number;
  refund_exchange_rate?: number;
  base_equivalent: number;
  exchange_difference: number;
  reference_number?: string;
  narration?: string;
  receiving_account_code?: string;
  receiving_account_name?: string;
  advance_gl_account_code?: string;
  advance_gl_account_name?: string;
  charge_lines: Array<{
    charge_type: string;
    account_code?: string;
    account_name?: string;
    amount: number;
    currency?: string;
    tax_code?: string;
    tax_amount?: number;
    remarks?: string;
  }>;
  charge_total: number;
  net_receipt: number;
  posted_at?: string | null;
}

const BASE = '/finance/supplier-advance-refunds';

export const REFUND_CHARGE_TYPES = [
  'Bank Charges',
  'SWIFT Charges',
  'Transfer Charges',
  'Currency Conversion Charges',
  'Supplier Deductions',
  'Documentation Charges',
  'Other Expenses',
] as const;

export function recoveryStatusTagColor(status: string | null | undefined): 'green' | 'orange' | 'red' | 'default' {
  const s = (status ?? '').toLowerCase();
  if (s.includes('fully') || s.includes('recovered')) return 'green';
  if (s.includes('partial')) return 'orange';
  if (s.includes('pending')) return 'red';
  return 'default';
}

export function refundVoucherStatusColor(status: string): string {
  if (status === 'Posted') return 'green';
  if (status === 'Cancelled') return 'red';
  if (status === 'Approved') return 'blue';
  return 'default';
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function computeFxPreview(
  refundAmount: number,
  bankReceived: number,
  advanceRate: number,
  refundRate: number
): { advanceBase: number; refundBase: number; exchangeDifference: number } {
  const advanceBase = roundMoney(refundAmount * (advanceRate || 1));
  const refundBase = roundMoney(bankReceived * (refundRate || 1));
  return {
    advanceBase,
    refundBase,
    exchangeDifference: roundMoney(refundBase - advanceBase),
  };
}

const supplierAdvanceRefundService = {
  async findAll(filters?: SupplierAdvanceRefundFilters): Promise<SupplierAdvanceRefundVoucher[]> {
    const res = await api.get(BASE, { params: filters });
    const list = unwrap<SupplierAdvanceRefundVoucher[]>(res);
    return Array.isArray(list) ? list : [];
  },

  async findById(id: string): Promise<SupplierAdvanceRefundVoucher> {
    const res = await api.get(`${BASE}/${id}`);
    return unwrap<SupplierAdvanceRefundVoucher>(res);
  },

  async create(payload: CreateSupplierAdvanceRefundPayload): Promise<SupplierAdvanceRefundVoucher> {
    const res = await api.post(BASE, payload);
    return unwrap<SupplierAdvanceRefundVoucher>(res);
  },

  async update(id: string, payload: UpdateSupplierAdvanceRefundPayload): Promise<SupplierAdvanceRefundVoucher> {
    const res = await api.patch(`${BASE}/${id}`, payload);
    return unwrap<SupplierAdvanceRefundVoucher>(res);
  },

  async post(id: string): Promise<SupplierAdvanceRefundVoucher> {
    const res = await api.post(`${BASE}/${id}/post`);
    return unwrap<SupplierAdvanceRefundVoucher>(res);
  },

  async cancel(id: string): Promise<SupplierAdvanceRefundVoucher> {
    const res = await api.post(`${BASE}/${id}/cancel`);
    return unwrap<SupplierAdvanceRefundVoucher>(res);
  },

  async getPrintData(id: string): Promise<SupplierAdvanceRefundPrintData> {
    const res = await api.get(`${BASE}/${id}/print`);
    return unwrap<SupplierAdvanceRefundPrintData>(res);
  },

  /** Linked journal voucher for accounting print (when posted). */
  async getPrintVoucherData(id: string): Promise<Record<string, unknown> | null> {
    const res = await api.get(`${BASE}/${id}/print-voucher`);
    const data = unwrap<Record<string, unknown> | null>(res);
    return data ?? null;
  },

  async findAllExposures(filters?: RecoveryExposureFilters): Promise<SupplierAdvanceRecoveryExposure[]> {
    const res = await api.get(`${BASE}/exposures`, { params: filters });
    const list = unwrap<SupplierAdvanceRecoveryExposure[]>(res);
    return Array.isArray(list) ? list : [];
  },

  async findExposureById(exposureId: string): Promise<SupplierAdvanceRecoveryExposure> {
    const res = await api.get(`${BASE}/exposures/${exposureId}`);
    return unwrap<SupplierAdvanceRecoveryExposure>(res);
  },

  async getRecoverySummary(): Promise<SupplierAdvanceRecoveryDashboardSummary> {
    const res = await api.get(`${BASE}/recovery-summary`);
    const data = unwrap<SupplierAdvanceRecoveryDashboardSummary>(res);
    return {
      pending_count: Number(data?.pending_count ?? 0),
      partial_count: Number(data?.partial_count ?? 0),
      overdue_count: Number(data?.overdue_count ?? 0),
      exposure_total: Number(data?.exposure_total ?? 0),
      queue_rows: Array.isArray(data?.queue_rows) ? data.queue_rows : [],
    };
  },
};

export default supplierAdvanceRefundService;
